import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { LifebuoyIcon } from '@phosphor-icons/react/dist/csr/Lifebuoy';
import { PaperPlaneTiltIcon } from '@phosphor-icons/react/dist/csr/PaperPlaneTilt';

import { Button } from '../../design-system/primitives/Button/Button';
import { CardHead } from '../../design-system/composites/Card/Card';
import { Textarea } from '../../design-system/primitives/Textarea/Textarea';
import { Select, type SelectOption } from '../../design-system/primitives/Select/Select';
import { ApiError, TOO_MANY_REQUESTS_CODE } from '../../lib/api/client';
import { supportApi, type SupportCategory } from '../../lib/api/support';
import styles from './SupportScreen.module.css';

/**
 * `/support` (M-15d) — a category picker and a message box, reachable by
 * every signed-in staff member with no permission gate (spec D-3): the
 * endpoint behind it carries none either.
 *
 * Built from the shared `Field`/`Select`/`Button` primitives rather than raw
 * elements — M-15c's review found a hand-rolled input forking `Field`'s
 * contract with three measured WCAG failures, and this screen exists to not
 * repeat that. The message box was the first control in the application to
 * compose `Field` directly rather than hand-roll a `<label>`/`<textarea>`
 * pair; that composition has since been promoted into a `Textarea` primitive
 * of its own and the thirteen dialogs that HAD hand-rolled the pair now use
 * it too, so this screen simply uses the primitive. The contract it was
 * getting from `Field` is unchanged — label association, `aria-describedby`,
 * `aria-invalid`, and the `role="alert"` announcement route.
 *
 * ## Fix round 1 — three things a first pass got wrong
 *
 * 1. **The success banner is now cleared at the START of every submit
 *    attempt** (`setSent(false)` beside `setError(null)`), not just on
 *    failure — otherwise a second successful send re-renders identical text
 *    with no DOM change (nothing announced), and a send that follows a
 *    successful one shows BOTH banners together, telling the user the
 *    message was both recorded and not sent. The `role="status"` region is
 *    also mounted UNCONDITIONALLY now, empty when there is nothing to say —
 *    `ReportsScreen.tsx`'s own `announcement()` region shape, and the same
 *    rule `UserActions.tsx`'s docblock states: "the live region mounted
 *    EMPTY from the first frame ... not inserted after a rejection".
 * 2. **The body's two server rules — `required`, `max:5000`
 *    (`StoreSupportMessageRequest::rules()`) — are now enforced client-side**
 *    via `Field`'s own `required`/`error` props (an empty submit never
 *    reaches the network at all) and a plain `maxLength` on the textarea.
 *    Deliberately NOT a validation framework and NOT a mirror of every
 *    server rule — just these two, because these two are the ones a screen
 *    that never round-trips can catch for free.
 * 3. **A `429` (D-8's dedicated rate limiter on this route) now gets its own
 *    sentence** (`support.throttled`) instead of sharing `support.failed`
 *    with every other cause — "Try again" is the one thing that is
 *    definitely false about a rate limit. Detected the way
 *    `UserActions.tsx`'s `describe()` already does: `error.code ===
 *    TOO_MANY_REQUESTS_CODE`, not a raw HTTP status the client would have to
 *    parse out of the thrown value itself.
 *
 * ## Final review — two more things fixed
 *
 * 1. **The controls are now wrapped in a real `<form>`** (`method="post"`,
 *    `noValidate` — the identical shape `LoginPage.tsx`/`UserFormDialog.tsx`
 *    use), with the submit button as `type="submit"`. Composing controls and
 *    a `Button` with no `<form>` meant Enter never submitted anything; the
 *    in-flight guard (`Button`'s own `loading` state, which swallows a
 *    second activation) is unchanged.
 * 2. **Two `role="alert"` regions could be live at once**: `Field`'s own
 *    alert for the `required` error, and this screen's own failure banner.
 *    They could not co-occur through the control flow at the time — but a
 *    STALE failure from an earlier attempt was never cleared by the
 *    empty-body guard, so failing once, clearing the box, then submitting
 *    empty again left BOTH alerts on screen simultaneously. `submit()` now
 *    clears `error` in that branch too, so the two are structurally
 *    exclusive rather than exclusive by coincidence.
 * 3. **The textarea now carries `dir="auto"`.** Unlike the reference
 *    dialogs, which fix `dir="ltr"`/`"rtl"` per field because they know each
 *    field's language, this box is free text in either language — an
 *    Arabic-UI agent pasting an English error string, a serial or a URL got
 *    bidi-reordered display without it.
 */

const CATEGORY_OPTIONS = [
  { value: 'bug', labelKey: 'support.categoryBug' },
  { value: 'data', labelKey: 'support.categoryData' },
  { value: 'access', labelKey: 'support.categoryAccess' },
  { value: 'other', labelKey: 'support.categoryOther' },
] as const satisfies readonly { value: SupportCategory; labelKey: string }[];

/** `StoreSupportMessageRequest::rules()`: `'body' => ['required', 'string', 'max:5000']`. */
const BODY_MAX_LENGTH = 5000;

export function SupportScreen() {
  const { t } = useTranslation();

  const [category, setCategory] = useState<SupportCategory>('bug');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bodyMissing, setBodyMissing] = useState(false);

  const categoryOptions: readonly SelectOption[] = CATEGORY_OPTIONS.map((option) => ({
    value: option.value,
    label: t(option.labelKey),
  }));

  async function submit(event: FormEvent<HTMLFormElement>) {
    // FIRST statement, the same reason `LoginPage.tsx`'s own handler gives:
    // everything below can throw, and a default-action submit that got
    // through would navigate — `method="post"` on the element is the second
    // half of that guard.
    event.preventDefault();

    // `Button`'s own busy guard only intercepts a CLICK; Enter submits the
    // form directly and does not necessarily route through it (the same
    // reason `LoginPage.tsx`'s handler checks `submitting` right after
    // `preventDefault` rather than relying on the button alone).
    if (busy) return;

    // Client-side half of `required` — caught here rather than round-tripped
    // to the server for a `422` this screen would only translate back into
    // the same generic sentence it now has better copy for.
    if (body.trim() === '') {
      setBodyMissing(true);
      // A stale failure from an earlier attempt must not sit beside the
      // required message on this one — see this file's "Final review" note.
      setError(null);
      return;
    }

    setBodyMissing(false);
    setBusy(true);
    setError(null);
    // Cleared on every attempt, not only a failing one — see this file's own
    // "Fix round 1" note above.
    setSent(false);
    try {
      await supportApi.send({ category, body });
      setSent(true);
      setBody('');
    } catch (thrown) {
      // The body is deliberately NOT cleared here: losing what the user just
      // wrote is a worse failure than the one being reported.
      setError(
        thrown instanceof ApiError && thrown.code === TOO_MANY_REQUESTS_CODE
          ? t('support.throttled')
          : t('support.failed'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className={styles['screen']}
      aria-labelledby="supportFormTitle"
      method="post"
      noValidate
      onSubmit={(event) => {
        void submit(event);
      }}
    >
      {/* The card's own head (the design language), not a restatement of the
          shell's `<h1>`: "Contact support" names the form, and the intro
          reads under it on the card. */}
      <CardHead id="supportFormTitle" icon={<LifebuoyIcon size={16} weight="duotone" />} title={t('support.title')} />
      <p className={styles['intro']}>{t('support.intro')}</p>

      <Select
        label={t('support.category')}
        width="phone"
        options={categoryOptions}
        value={category}
        onChange={(event) => {
          setCategory(event.target.value as SupportCategory);
        }}
      />

      <Textarea
        label={t('support.message')}
        required
        error={bodyMissing ? t('form.requiredField') : undefined}
        rows={6}
        // Free text in either language, unlike the reference dialogs'
        // per-field dir="ltr"/"rtl" — see this file's "Final review" note.
        dir="auto"
        maxLength={BODY_MAX_LENGTH}
        value={body}
        onChange={(event) => {
          setBody(event.target.value);
        }}
      />

      {/*
        Mounted in every state, empty when it has nothing to say — the
        `ReportsScreen.tsx` `announcement()` shape, so a SECOND successful
        send (identical text, otherwise no DOM change) is still announced,
        and the region is never inserted already populated.
      */}
      <p role="status" className={styles['sent']}>
        {sent ? t('support.sent') : null}
      </p>

      {error !== null && (
        <p role="alert" className={styles['failure']}>
          {error}
        </p>
      )}

      <div className={styles['actions']}>
        <Button type="submit" loading={busy} icon={<PaperPlaneTiltIcon size={16} />}>
          {busy ? t('support.sending') : t('support.submit')}
        </Button>
      </div>
    </form>
  );
}
